import { NavigateFunction } from 'react-router-dom';

import * as amplitude from '@amplitude/analytics-browser';

import { LinkPage, handleLeaveLink } from '../components/ButtonExitLink';

export enum OnboardErrorType {
  INVALID_CREDENTIALS = 'invalid_credentials',
  CONNECTION_FAILED = 'connection_failed',
  ACCOUNT_NOT_FOUND = 'account_not_found',
  PERMISSION_DENIED = 'permission_denied',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  RATE_LIMITED = 'rate_limited',
  VALIDATION_ERROR = 'validation_error',
  UNKNOWN = 'unknown',
}

export interface OnboardErrorDetails {
  type: OnboardErrorType;
  message: string;
  caption: string;
  showInline?: boolean; // Whether to show as inline alert vs redirect to error page
  retryable?: boolean;
  helpText?: string;
  showEscapeHatch?: boolean; // Whether to show "Skip" option for inline errors
}

export interface OnboardErrorContext {
  tsp: string;
  serviceName: string;
  isUpdating?: boolean;
  formData?: Record<string, any>;
}

export interface OnboardErrorHandlers {
  navigate: NavigateFunction;
  setShowError: (show: boolean) => void;
  setErrorText: (text: string) => void;
  setErrorDetails?: (details: OnboardErrorDetails) => void;
  onSkip?: () => void;
  search?: string;
  analyticsContext?: any; // Analytics context for tracking errors
}

export interface OnboardSkipContext {
  token: string;
  redirectURIError?: string;
  serviceName: string;
  tsp: string;
}

/**
 * Predefined error patterns for onboarding scenarios
 */
export const ONBOARD_ERROR_PATTERNS: Record<string, OnboardErrorDetails> = {
  // Credential-related errors (show inline)
  INVALID_CREDENTIALS: {
    type: OnboardErrorType.INVALID_CREDENTIALS,
    message: 'Account not found',
    caption: "We couldn't find an account with these credentials.",
    showInline: true,
    retryable: true,
    showEscapeHatch: true,
    helpText:
      'Please verify that your account is active and the credentials are correct.',
  },

  ACCOUNT_NOT_FOUND: {
    type: OnboardErrorType.ACCOUNT_NOT_FOUND,
    message: 'Account not found',
    caption: "We couldn't find an account with these credentials.",
    showInline: true,
    retryable: true,
    showEscapeHatch: true,
    helpText:
      'Please verify that your account is active and the credentials are correct.',
  },

  PERMISSION_DENIED: {
    type: OnboardErrorType.PERMISSION_DENIED,
    message: 'Access denied',
    caption: "Your account doesn't have the required permissions.",
    showInline: true,
    retryable: false,
    showEscapeHatch: true,
    helpText:
      'Contact your fleet administrator to ensure your account has API access enabled.',
  },

  VALIDATION_ERROR: {
    type: OnboardErrorType.VALIDATION_ERROR,
    message: 'Invalid information',
    caption: 'Please check all required fields and try again.',
    showInline: true,
    retryable: true,
    showEscapeHatch: true,
  },

  // System-related errors (redirect to error page)
  CONNECTION_FAILED: {
    type: OnboardErrorType.CONNECTION_FAILED,
    message: 'Connection failed',
    caption: 'Unable to connect to your telematics provider. Please try again.',
    showInline: false,
    retryable: true,
  },

  SERVICE_UNAVAILABLE: {
    type: OnboardErrorType.SERVICE_UNAVAILABLE,
    message: 'Service temporarily unavailable',
    caption:
      'The telematics service is currently unavailable. Please try again later.',
    showInline: false,
    retryable: true,
  },

  RATE_LIMITED: {
    type: OnboardErrorType.RATE_LIMITED,
    message: 'Too many attempts',
    caption: 'Please wait a few minutes before trying again.',
    showInline: false,
    retryable: true,
  },

  UNKNOWN: {
    type: OnboardErrorType.UNKNOWN,
    message: 'Something went wrong',
    caption:
      'An unexpected error occurred. Please try again or contact support.',
    showInline: false,
    retryable: true,
  },
};

/**
 * Analyze error response and categorize it
 */
export function categorizeOnboardError(
  error: any,
  context: OnboardErrorContext
): OnboardErrorDetails {
  const errorMessage = error?.response?.data || error?.message || '';
  const statusCode = error?.response?.status;

  // First, check for specific client-side app missing errors - these should be preserved exactly
  if (
    errorMessage.includes(
      'app is associated with this account - please contact Axle support'
    )
  ) {
    return {
      type: OnboardErrorType.PERMISSION_DENIED,
      message: errorMessage, // Preserve the exact error message
      caption: 'This provider requires additional setup.',
      showInline: true,
      retryable: false,
      showEscapeHatch: true,
      helpText:
        'Please contact support to enable this integration for your account.',
    };
  }

  // Check for specific error patterns
  if (
    statusCode === 401 ||
    errorMessage.toLowerCase().includes('invalid') ||
    errorMessage.toLowerCase().includes('unauthorized') ||
    errorMessage.toLowerCase().includes('credential') ||
    errorMessage.toLowerCase().includes('error occurred while logging') ||
    errorMessage.toLowerCase().includes('login failed') ||
    errorMessage.toLowerCase().includes('authentication failed')
  ) {
    return ONBOARD_ERROR_PATTERNS.INVALID_CREDENTIALS;
  }

  if (
    statusCode === 404 ||
    errorMessage.toLowerCase().includes('not found') ||
    errorMessage.toLowerCase().includes('account')
  ) {
    return ONBOARD_ERROR_PATTERNS.ACCOUNT_NOT_FOUND;
  }

  if (
    statusCode === 403 ||
    errorMessage.toLowerCase().includes('permission') ||
    errorMessage.toLowerCase().includes('access denied')
  ) {
    return ONBOARD_ERROR_PATTERNS.PERMISSION_DENIED;
  }

  if (
    statusCode === 429 ||
    errorMessage.toLowerCase().includes('rate limit') ||
    errorMessage.toLowerCase().includes('too many')
  ) {
    return ONBOARD_ERROR_PATTERNS.RATE_LIMITED;
  }

  if (
    statusCode >= 500 ||
    errorMessage.toLowerCase().includes('server') ||
    errorMessage.toLowerCase().includes('unavailable')
  ) {
    return ONBOARD_ERROR_PATTERNS.SERVICE_UNAVAILABLE;
  }

  if (statusCode >= 400 && statusCode < 500) {
    return ONBOARD_ERROR_PATTERNS.VALIDATION_ERROR;
  }

  if (
    !error?.response &&
    (errorMessage.toLowerCase().includes('network') ||
      errorMessage.toLowerCase().includes('connection'))
  ) {
    return ONBOARD_ERROR_PATTERNS.CONNECTION_FAILED;
  }

  // Default to unknown error
  return ONBOARD_ERROR_PATTERNS.UNKNOWN;
}

/**
 * Handle onboard error with appropriate UX pattern
 */
export async function handleOnboardError(
  error: any,
  context: OnboardErrorContext,
  handlers: OnboardErrorHandlers
): Promise<OnboardErrorDetails> {
  const errorDetails = categorizeOnboardError(error, context);

  // Track the error in Amplitude
  amplitude.track('Onboarding Error Occurred', {
    errorType: errorDetails.type,
    errorMessage: errorDetails.message,
    tsp: context.tsp,
    serviceName: context.serviceName,
    isUpdating: context.isUpdating,
    showInline: errorDetails.showInline,
    retryable: errorDetails.retryable,
  });

  // Track the error in analytics system (for Slack and webhooks)
  // This will send the failure notification to Slack and trigger failure webhook
  if (handlers.analyticsContext) {
    try {
      const { Analytics } = await import('./analytics');

      await Analytics.connectionError(
        handlers.analyticsContext,
        errorDetails.type,
        errorDetails.message,
        'Onboard',
        errorDetails // Pass the full errorDetails object for enhanced Slack notifications
      );
    } catch (analyticsError) {
      // Graceful failure - don't break error handling
      console.warn(
        '[OnboardError] Failed to track error in analytics:',
        analyticsError
      );
    }
  }

  if (errorDetails.showInline) {
    // Show inline error alert
    handlers.setErrorText(errorDetails.message);
    handlers.setShowError(true);

    // Pass error details for enhanced alert component
    if (handlers.setErrorDetails) {
      handlers.setErrorDetails(errorDetails);
    }
  } else {
    // Navigate to dedicated error page
    handlers.navigate('/error', {
      state: {
        errorMsg: errorDetails.message,
        errorCaption: errorDetails.caption,
        errorType: errorDetails.type,
        returnPath: `/config/${context.tsp}`,
        showTryAgain: errorDetails.retryable,
        showSkip: true,
        helpText: errorDetails.helpText,
      },
      ...(handlers.search && { search: handlers.search }),
    });
  }

  return errorDetails;
}

/**
 * Handle onboard success with enhanced UX
 */
export function handleOnboardSuccess(
  context: OnboardErrorContext & { redirectURI?: string },
  handlers: {
    navigate: NavigateFunction;
    setOnboarded: (onboarded: boolean) => void;
    search?: string;
  }
): void {
  // Track success
  amplitude.track('Onboarding Success Occurred', {
    tsp: context.tsp,
    serviceName: context.serviceName,
    isUpdating: context.isUpdating,
    hasRedirectURI: !!context.redirectURI,
  });

  // Set onboarded state
  handlers.setOnboarded(true);

  // Navigate to success page with context
  handlers.navigate('/success', {
    state: {
      successMsg: `${getTSPDisplayName(
        context.tsp
      )} account connected successfully!`,
      successCaption: context.isUpdating
        ? 'Your account has been updated and is ready to use.'
        : 'Your telematics data is now connected and ready to use.',
      tsp: context.tsp,
      isUpdating: context.isUpdating,
    },
    ...(handlers.search && { search: handlers.search }),
  });
}

/**
 * Get display name for TSP
 */
function getTSPDisplayName(tsp: string): string {
  const displayNames: Record<string, string> = {
    motive: 'Motive',
    samsara: 'Samsara',
    zubie: 'Zubie',
    bouncie: 'Bouncie',
    monarchtracking: 'Monarch Tracking',
    txtesolution: 'TXTESolution',
    // Add more as needed
  };

  return displayNames[tsp] || tsp.charAt(0).toUpperCase() + tsp.slice(1);
}

/**
 * Handle skip action from inline error escape hatch
 */
export async function handleOnboardSkip(
  context: OnboardSkipContext
): Promise<void> {
  // Track the skip action in Amplitude
  amplitude.track('User Skipped from Inline Error', {
    tsp: context.tsp,
    serviceName: context.serviceName,
    source: 'inline_error_escape_hatch',
  });

  // Use the analytics system to track ERROR_QUIT event and handle redirect
  // This will trigger the failure webhook AND handle the redirect
  try {
    const { Analytics } = await import('./analytics');

    // Use the confirmed skip function which handles both webhook and redirect
    await Analytics.confirmedSkip.errorQuit(
      {
        serviceName: context.serviceName,
        tsp: context.tsp,
        token: context.token,
        redirectURIError: context.redirectURIError,
      },
      'user_quit', // errorType
      'Onboard' // page
    );
  } catch (error) {
    // Fallback to direct redirect if analytics fails
    console.warn(
      '[OnboardSkip] Analytics failed, falling back to direct redirect:',
      error
    );
    handleLeaveLink({
      token: context.token,
      redirectURIError: context.redirectURIError || '/error',
      serviceName: context.serviceName,
      pageOnExit: LinkPage.OnboardingCreds,
      selectedTSP: context.tsp,
    });
  }
}

/**
 * Enhanced error message with help text
 */
export function getEnhancedErrorMessage(
  errorDetails: OnboardErrorDetails,
  context: OnboardErrorContext
): string {
  let message = errorDetails.message;

  if (errorDetails.helpText) {
    message += ` ${errorDetails.helpText}`;
  }

  return message;
}
